import { LightningElement } from 'lwc';

import { fetchAuthSession } from 'aws-amplify/auth';
import { get, post } from 'aws-amplify/api';

function formatStatus(status) {
    if (!status) return '';
    return status.charAt(0).toUpperCase() + status.slice(1);
}

export default class GetWetMessageCenter extends LightningElement {
    _isLoading = false;
    _idToken;
    threads = [];
    replyDrafts = {};
    toastObj;

    get hasThreads() {
        return this.threads.length > 0;
    }

    async connectedCallback() {
        await this.loadMessages();
    }

    async getIdToken() {
        if (!this._idToken) {
            const session = await fetchAuthSession();
            this._idToken = session.tokens?.idToken?.toString();
        }
        return this._idToken;
    }

    async loadMessages() {
        this._isLoading = true;

        try {
            const idToken = await this.getIdToken();
            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }

            const op = get({
                apiName: 'api',
                path: '/cognito/messages',
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                },
            });

            const { body } = await op.response;
            const payload = await body.json();
            this.threads = this.formatThreads(payload?.threads || []);
            this.dispatchUnreadMessagesChange(0);
        } catch (error) {
            this.showToast('Error loading messages', error?.message || error, 'error');
        } finally {
            this._isLoading = false;
        }
    }

    formatThreads(threads = []) {
        return threads.map(thread => {
            const hostName = thread.host_display_name
                || [thread.host_first_name, thread.host_last_name].filter(Boolean).join(' ')
                || thread.host_email
                || 'Host';
            const replyBody = this.replyDrafts[thread.thread_id] || '';
            const location = [thread.city, thread.state].filter(Boolean).join(', ');

            return {
                ...thread,
                hostName,
                location,
                replyBody,
                isReplyDisabled: !replyBody.trim(),
                statusLabel: formatStatus(thread.reservation_status),
                lastMessageAtLabel: thread.last_message_at
                    ? new Date(thread.last_message_at).toLocaleString()
                    : '',
                messages: (thread.messages || []).map(message => ({
                    ...message,
                    messageClass: message.is_from_guest
                        ? 'message-center-message message-center-message_guest'
                        : 'message-center-message',
                    senderLabel: message.is_from_guest ? 'You' : hostName,
                    createdAtLabel: message.created_at
                        ? new Date(message.created_at).toLocaleString()
                        : ''
                }))
            };
        });
    }

    handleReplyChange(event) {
        const threadId = event.currentTarget?.dataset?.threadId;
        if (!threadId) return;

        this.replyDrafts = {
            ...this.replyDrafts,
            [threadId]: event.target.value
        };
        this.threads = this.threads.map(item => (
            item.thread_id === threadId
                ? {
                    ...item,
                    replyBody: event.target.value,
                    isReplyDisabled: !event.target.value.trim()
                }
                : item
        ));
    }

    async handleSendReply(event) {
        const threadId = event.currentTarget?.dataset?.threadId;
        const thread = this.threads.find(item => item.thread_id === threadId);
        const replyBody = String(thread?.replyBody || '').trim();
        if (!threadId || !replyBody) return;

        this._isLoading = true;

        try {
            const idToken = await this.getIdToken();
            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }

            const op = post({
                apiName: 'api',
                path: '/cognito/messages/reply',
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: {
                        threadId,
                        body: replyBody
                    }
                }
            });

            const { body } = await op.response;
            const payload = await body.json();
            const message = payload?.message;
            if (!message) {
                throw new Error('Message reply failed');
            }

            this.replyDrafts = {
                ...this.replyDrafts,
                [threadId]: ''
            };
            this.threads = this.threads.map(item => {
                if (item.thread_id !== threadId) return item;

                const createdAt = message.created_at || new Date().toISOString();
                const nextThread = {
                    ...item,
                    last_message_at: createdAt,
                    messages: [
                        ...(item.messages || []),
                        {
                            ...message,
                            is_from_guest: true,
                            created_at: createdAt
                        }
                    ]
                };
                return this.formatThreads([nextThread])[0];
            });
            this.clearReplyTextarea(threadId);
        } catch (error) {
            this.showToast('Error sending reply', error?.message || error, 'error');
        } finally {
            this._isLoading = false;
        }
    }

    clearReplyTextarea(threadId) {
        const textarea = this.template.querySelector(`textarea[data-thread-id="${threadId}"]`);
        if (textarea) {
            textarea.value = '';
        }
    }

    dispatchUnreadMessagesChange(unreadMessageCount) {
        this.dispatchEvent(new CustomEvent('unreadmessageschange', {
            detail: {
                unreadMessageCount
            },
            bubbles: true,
            composed: true
        }));
    }

    showToast(title, message, variant, mode=null) {
        const modeP = mode
            ? mode
            : variant === 'success'
                ? 'dismissible'
                : 'sticky';

        this.toastObj = {
            variant,
            mode: modeP,
            message,
            header: {
                text: title
            }
        };
    }
}
